import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ja, type TranslationKeys } from '../translations/ja';
import { en } from '../translations/en';

export type Language = 'ja' | 'en';

interface LanguageContextType {
	language: Language;
	t: TranslationKeys;
	toggleLanguage: () => void;
	setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// ローカルストレージのキー
const LANGUAGE_STORAGE_KEY = 'portfolio-language';

// ブラウザの言語設定を取得
const getBrowserLanguage = (): Language => {
	if (typeof window === 'undefined') return 'ja';
	const browserLang = navigator.language.toLowerCase();
	return browserLang.startsWith('ja') ? 'ja' : 'en';
};

// 保存された言語またはブラウザ言語を取得
const getSavedLanguage = (): Language => {
	if (typeof window === 'undefined') return 'ja';
	const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language | null;
	return saved || getBrowserLanguage();
};

// 翻訳マップ
const translations: Record<Language, TranslationKeys> = {
	ja,
	en: en as any as TranslationKeys,
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const [language, setLanguageState] = useState<Language>(getSavedLanguage());
	const t = translations[language];

	// 言語変更時にローカルストレージに保存
	useEffect(() => {
		localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
		// html要素のlang属性を更新（SEO対応）
		document.documentElement.lang = language;
	}, [language]);

	const toggleLanguage = () => {
		setLanguageState(prev => prev === 'ja' ? 'en' : 'ja');
	};

	const setLanguage = (lang: Language) => {
		setLanguageState(lang);
	};

	return (
		<LanguageContext.Provider value={{ language, t, toggleLanguage, setLanguage }}>
			{children}
		</LanguageContext.Provider>
	);
};

export const useLanguage = (): LanguageContextType => {
	const context = useContext(LanguageContext);
	if (!context) {
		throw new Error('useLanguage must be used within a LanguageProvider');
	}
	return context;
};
